import { ROSELoader } from './rose';
import { BinaryReader } from '../util/binaryreader';
import { BinaryWriter } from '../util/binarywriter';

/**
 * @property {String} name
 * @property {Number} id
 * @property {String} filePath
 * @property {Number} lightmapIndex
 * @property {Number} pixelsPerObject
 * @property {Number} objectsPerRow
 * @property {Number} objectIndex
 */
export class LightmapObjectPart {
  name: string = '';
  filePath: string = '';
  lightmapIndex: number = 0;
  pixelsPerObject: number = 0;
  objectsPerRow: number = 0;
  objectIndex: number = 0;
}

/**
 * @property {Number} id
 * @property {Object.<number,LightmapObjectPart>} parts
 */
export class LightmapObject {
  parts: { [key: number]: LightmapObjectPart } = {};
  partOrder: number[] = [];
}

/**
 * @property {Object.<number,LightmapObject>} objects
 * @property {String[]} textures
 */
export class LightmapData {
  objects: { [key: number]: LightmapObject } = {};
  textures: string[] = [];
  /** Texture names exactly as stored in the LIT, before folder resolution. */
  textureNames: string[] = [];
  objectOrder: number[] = [];

  static parse(rh: BinaryReader, folderPath = ''): LightmapData {
    const data = new LightmapData();

    const objects = rh.readUint32();
    for (let i = 0; i < objects; ++i) {
      const object = new LightmapObject();
      const partsCount = rh.readUint32();
      const objectId = rh.readUint32() - 1;
      data.objectOrder.push(objectId);
      for (let j = 0; j < partsCount; ++j) {
        const part = new LightmapObjectPart();
        part.name            = rh.readUint8Str();
        const partId         = rh.readUint32();
        part.filePath        = rh.readUint8Str();
        part.lightmapIndex   = rh.readUint32();
        part.pixelsPerObject = rh.readUint32();
        part.objectsPerRow   = rh.readUint32();
        part.objectIndex     = rh.readUint32();
        object.parts[partId] = part;
        object.partOrder.push(partId);
      }
      data.objects[objectId] = object;
    }

    const textures = rh.readUint32();
    for (let i = 0; i < textures; ++i) {
      const textureName = rh.readUint8Str();
      data.textureNames.push(textureName);
      data.textures.push(folderPath + textureName);
    }
    return data;
  }

  static serialize(data: LightmapData): ArrayBuffer {
    const writer = new BinaryWriter();
    const listedObjects = new Set<number>();
    const objectIds: number[] = [];
    for (const id of data.objectOrder) {
      if (data.objects[id] && !listedObjects.has(id)) {
        objectIds.push(id);
        listedObjects.add(id);
      }
    }
    for (const key of Object.keys(data.objects)) {
      const id = Number(key);
      if (!listedObjects.has(id)) objectIds.push(id);
    }

    writer.writeUint32(objectIds.length);
    for (const objectId of objectIds) {
      const object = data.objects[objectId];
      const listedParts = new Set<number>();
      const partIds: number[] = [];
      for (const id of object.partOrder) {
        if (object.parts[id] && !listedParts.has(id)) {
          partIds.push(id);
          listedParts.add(id);
        }
      }
      for (const key of Object.keys(object.parts)) {
        const id = Number(key);
        if (!listedParts.has(id)) partIds.push(id);
      }

      writer.writeUint32(partIds.length);
      writer.writeUint32(objectId + 1);
      for (const partId of partIds) {
        const part = object.parts[partId];
        writer.writeUint8Str(part.name || '');
        writer.writeUint32(partId);
        writer.writeUint8Str(part.filePath || '');
        writer.writeUint32(part.lightmapIndex);
        writer.writeUint32(part.pixelsPerObject);
        writer.writeUint32(part.objectsPerRow);
        writer.writeUint32(part.objectIndex);
      }
    }

    const textureNames = data.textureNames.length === data.textures.length
      ? data.textureNames
      : data.textures.map((texture) => texture.replace(/\\/g, '/').split('/').pop() || texture);
    writer.writeUint32(textureNames.length);
    for (const textureName of textureNames) writer.writeUint8Str(textureName);
    return writer.toArrayBuffer();
  }

  /**
   * @param {String} path
   * @param {Function} callback
   */
  static load(path: any, callback: any) {
    const folderPath = path.substr(0, path.lastIndexOf('/') + 1);
    ROSELoader.load(path, function(rh: any) {
      callback(LightmapData.parse(rh, folderPath));
    });
  }
}
